import { useRef, useState, type KeyboardEvent } from 'react';

import type { OrganisationLocation, OrganisationTeam, Tenant } from '../domain-types';
import { organisationDisplayName } from '../utils/organisation-display';
import { OrganisationStructurePanel } from './tenant-panel';

export function OrganisationPage({
  tenant,
  editable,
  busy,
  onSaveStructure,
}: {
  tenant: Tenant | null;
  editable: boolean;
  busy: boolean;
  onSaveStructure: (structure: {
    locations: OrganisationLocation[];
    teams: OrganisationTeam[];
  }) => Promise<void>;
}) {
  const [activeTab, setActiveTab] = useState<'overview' | 'structure'>('overview');
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const tabs = [
      ...(tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []),
    ];
    if (tabs.length === 0) return;

    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    const nextValue = nextTab?.dataset.tab as 'overview' | 'structure' | undefined;
    if (!nextTab || !nextValue) return;
    setActiveTab(nextValue);
    nextTab.focus();
  };

  if (!tenant) {
    return (
      <section className="organisation-page" aria-labelledby="organisation-page-title">
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Organisation</p>
            <h2 id="organisation-page-title">Organisation</h2>
          </div>
          <span>View only</span>
        </div>
        <p className="empty-state">No organisation is currently selected.</p>
      </section>
    );
  }

  return (
    <div className="organisation-page-layout">
      <section className="organisation-page" aria-labelledby="organisation-page-title">
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Organisation</p>
            <h2 id="organisation-page-title">{organisationDisplayName(tenant.name)}</h2>
          </div>
          <span>{editable ? 'Organisation Administrator' : 'View only'}</span>
        </div>
        <div
          aria-label="Organisation sections"
          className="organisation-page-tabs"
          ref={tabListRef}
          role="tablist"
          onKeyDown={handleTabKeyDown}
        >
          <button
            aria-controls="organisation-overview-panel"
            aria-selected={activeTab === 'overview'}
            className={activeTab === 'overview' ? 'active' : undefined}
            data-tab="overview"
            id="organisation-overview-tab"
            role="tab"
            tabIndex={activeTab === 'overview' ? 0 : -1}
            type="button"
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            aria-controls="organisation-structure-panel"
            aria-selected={activeTab === 'structure'}
            className={activeTab === 'structure' ? 'active' : undefined}
            data-tab="structure"
            id="organisation-structure-tab"
            role="tab"
            tabIndex={activeTab === 'structure' ? 0 : -1}
            type="button"
            onClick={() => setActiveTab('structure')}
          >
            Structure
          </button>
        </div>
        {activeTab === 'overview' ? (
          <div
            aria-labelledby="organisation-overview-tab"
            id="organisation-overview-panel"
            role="tabpanel"
          >
            <dl className="organisation-page-summary">
              <div>
                <dt>Organisation name</dt>
                <dd>{organisationDisplayName(tenant.name)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{tenant.status.replaceAll('_', ' ')}</dd>
              </div>
              <div>
                <dt>Organisation reference</dt>
                <dd>{tenant.slug}</dd>
              </div>
            </dl>
          </div>
        ) : null}
      </section>
      {activeTab === 'structure' ? (
        <div
          aria-labelledby="organisation-structure-tab"
          id="organisation-structure-panel"
          role="tabpanel"
        >
          <OrganisationStructurePanel
            busy={busy}
            editable={editable}
            locations={tenant.locations ?? []}
            onSave={onSaveStructure}
            teams={tenant.teams ?? []}
            visible
          />
        </div>
      ) : null}
    </div>
  );
}
